import { put, takeEvery } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { first, get, isNil, map } from 'lodash';
import { warn } from 'js-logger';

import { openFlashMessage } from 'components/utils/flashMessages';
import { openModal } from 'containers/App/actions/ui';

import { fetchCounters } from 'containers/App/actions/sessions';
import { FlashDefinition } from 'containers/App/utils/flashDefinition';
import {
  isPublicJobBoardContext,
  logoutRedirect,
} from 'containers/App/utils/helpers';
import { isAdministrator } from 'utils/authorization/utils/auth';
import { getServerEnv } from 'utils/checkEnvironment';
import { SIGN_IN_ERROR } from 'containers/Login/constants';

export function* handleFailure(action) {
  const { type, error, errorMessage } = action;
  warn(`[Redux] Error action: ${type}`, action);

  if (isNil(error) || isNil(error.response)) return;

  if (error.message === 'Network Error') {
    warn('[API] 0: Connection Problem');

    openFlashMessage(FlashDefinition.NetworkError);
    return;
  }

  if (isNil(error.response.status) && isNil(error.response.data)) return;

  const { status, data } = error.response;
  const errMsgKey = errorMessage || data.error || first(data.errors);
  const shouldDisplayUnknownErrors =
    getServerEnv(window) !== 'prod' || isAdministrator();
  const publicJobBoardContext = isPublicJobBoardContext(window.location);

  switch (true) {
    case status === 302:
      warn('[API] 302: Found');

      if (data.redirect_url) {
        yield put(push(data.redirect_url));
      } else if (shouldDisplayUnknownErrors) {
        openFlashMessage(FlashDefinition.UnknownError);
      }
      break;

    case status === 401:
    case status === 403:
      warn('[API] 401/403: Unauthorized');

      if (data.error === 'session_expired') {
        yield put(push(logoutRedirect()));
        if (!publicJobBoardContext) {
          openFlashMessage(
            get(FlashDefinition, errMsgKey, FlashDefinition.sessionExpired),
            { id: 'sessionExpired' }
          );
        }
        break;
      }

      if (type !== SIGN_IN_ERROR) {
        const preserveUrl = data.error === 'invalid_credentials';
        yield put(push(logoutRedirect(preserveUrl)));
      }

      if (!publicJobBoardContext || data.error !== 'invalid_credentials') {
        openFlashMessage(
          get(FlashDefinition, errMsgKey, FlashDefinition.UnauthorizedError)
        );
      }
      break;

    case status === 408:
      warn('[API] 408: Request Timeout');

      openFlashMessage(FlashDefinition.RequestTimeoutError);
      break;

    case status === 418:
      warn('[API] 418: I’m a teapot');

      openFlashMessage(
        get(FlashDefinition, errMsgKey, FlashDefinition.ValidationError)
      );
      break;

    case status === 422:
      warn('[API] 422: Validation Error');

      if (data.error === 'expired_token') {
        if (!publicJobBoardContext) {
          openFlashMessage(FlashDefinition.expiredToken);
        }
        yield put(push(logoutRedirect(false)));
      } else if (data.error === 'consumed_email_confirm_token') {
        openFlashMessage(FlashDefinition.consumedEmailConfirmationToken);
        yield put(push('/logout'));
      } else if (data.error === 'consumed_token') {
        if (!publicJobBoardContext) {
          openFlashMessage(FlashDefinition.consumedToken);
        }
        yield put(push(logoutRedirect(false)));
      } else if (
        data.error === 'bullhorn_token_refresh_failed' ||
        data.error === 'bullhorn_not_connected'
      ) {
        yield put(openModal({ type: 'bullhornAuth' }));
      } else if (data.error === 'resend_blocked') {
        openFlashMessage(FlashDefinition.ResendBlocked);
      } else if (data.error === 'limit_exceeded') {
        warn('[API] CV parse limit exceeded (handled by caller)', data);
      } else if (data.error || (data.errors && data.errors.length === 1)) {
        openFlashMessage(
          get(FlashDefinition, errMsgKey, FlashDefinition.ValidationError)
        );
      }

      if (data.errors && data.errors.length > 1) {
        const errors = map(data.errors, (err) => err.source.pointer);

        // TODO: Implement generic error message lookup, quick fix use code 418
        warn('[API] Multiple errors occurred', errors);
      }
      break;

    case status > 400 && status < 500:
      warn('[API] 4xx: Internal Client Error');

      if (shouldDisplayUnknownErrors && !publicJobBoardContext) {
        openFlashMessage(FlashDefinition.ClientError);
      }

      break;

    case status >= 500:
      warn('[API] 5xx: Internal Server Error');

      if (shouldDisplayUnknownErrors) {
        openFlashMessage(FlashDefinition.ServerError);
      }

      break;

    default:
      warn(`[API] ${status}: Unknown Error`);

      if (shouldDisplayUnknownErrors) {
        openFlashMessage(FlashDefinition.UnknownError);
      }
  }
}

export function* handleUpdates() {
  yield put(fetchCounters());
}

export function* rootSaga() {
  yield takeEvery((action) => /ERROR$/.test(action.type), handleFailure);
  yield takeEvery((action) => action.forceCountersUpdate, handleUpdates);
}
